import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const TrialBalance = () => {
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(new Date());
    const [trialBalanceData, setTrialBalanceData] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState('KES');
    const [searchTerm, setSearchTerm] = useState('');
    const [accountTypes, setAccountTypes] = useState([]);
    const [selectedAccountType, setSelectedAccountType] = useState(null);
    const [glEntries, setGlEntries] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const toast = useRef(null);

    const facilityId = "68354b430bfa0b7ac72078c5"; // Updated to match your facility ID

    useEffect(() => {
        fetchAccountsAndEntries();
    }, [startDate, endDate]);

    const fetchAccountsAndEntries = async () => {
        try {
            setLoading(true);
            console.log('Fetching accounts and GL entries for facility:', facilityId);

            // Fetch accounts first and wait for completion
            const fetchedAccounts = await fetchAccounts();

            // Then fetch GL entries and process with the fetched accounts
            await fetchGLEntries(fetchedAccounts);

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch data: ' + (error.message || 'Unknown error')
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await makeRequest2(`/api/v1/gl-accounts/${facilityId}`, 'GET');

            let fetchedAccounts = [];
            if (response?.data?.data) {
                fetchedAccounts = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedAccounts = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedAccounts = response;
            }

            console.log('Fetched accounts:', fetchedAccounts);
            setAccounts(fetchedAccounts);

            // Return the accounts so they can be used immediately
            return fetchedAccounts;

        } catch (error) {
            console.error('Error fetching accounts:', error);
            throw error; // Re-throw to be caught by parent function
        }
    };

    const fetchGLEntries = async (accountsData = null) => {
        try {
            console.log('Fetching GL entries for facility:', facilityId);

            // Use passed accounts data or fall back to state
            const accountsToUse = accountsData || accounts;

            // Build query parameters for date filtering
            const queryParams = new URLSearchParams();
            if (startDate) {
                queryParams.append('startDate', startDate.toISOString());
            }
            if (endDate) {
                queryParams.append('endDate', endDate.toISOString());
            }

            // Fixed endpoint URL (removed extra curly brace)
            const endpoint = `/api/v1/gl-entries/get-all-entries/${facilityId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
            const response = await makeRequest2(endpoint, 'GET');

            console.log('API response:', response);

            let fetchedEntries = [];
            if (response?.data?.data) {
                fetchedEntries = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedEntries = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedEntries = response;
            }

            console.log('Processed GL entries:', fetchedEntries);
            setGlEntries(fetchedEntries);

            // Process the entries into trial balance format using the accounts data
            processTrialBalanceData(fetchedEntries, accountsToUse);

            const dateRangeText = startDate && endDate
                ? `from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`
                : endDate
                    ? `up to ${endDate.toLocaleDateString()}`
                    : 'for all dates';

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: `${fetchedEntries.length} entries loaded successfully ${dateRangeText}`,
                life: 3000
            });

        } catch (error) {
            console.error('Error fetching GL entries:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch GL entries: ' + (error.message || 'Unknown error')
            });
            throw error; // Re-throw to be caught by parent function
        }
    };

    const processTrialBalanceData = (entries, accountsData) => {
        if (!entries || entries.length === 0) {
            // If no entries, show empty trial balance
            setTrialBalanceData({
                asOfDate: endDate ? endDate.toLocaleDateString() : new Date().toLocaleDateString(),
                dateRange: {
                    start: startDate ? startDate.toLocaleDateString() : null,
                    end: endDate ? endDate.toLocaleDateString() : null
                },
                accounts: [],
                totalDebit: 0,
                totalCredit: 0,
                isBalanced: true
            });
            return;
        }

        // Ensure we have accounts data
        if (!accountsData || accountsData.length === 0) {
            console.warn('No accounts data available for processing trial balance');
            setTrialBalanceData(null);
            return;
        }

        // Group entries by accountId and sum amounts
        const accountMap = {};

        // Process each entry
        entries.forEach(entry => {
            const { accountId, amount, entryType } = entry;

            if (!accountMap[accountId]) {
                // Find account details from accounts array
                const accountDetails = accountsData.find(acc => acc._id === accountId);

                accountMap[accountId] = {
                    accountId: accountId,
                    code: accountDetails?.accountCode || accountId.substring(accountId.length - 4),
                    name: accountDetails?.accountName || `Account ${accountId.substring(accountId.length - 6)}`,
                    accountType: accountDetails?.accountType?.toLowerCase() || 'unknown',
                    openingBalance: accountDetails?.accountBalance || 0,
                    debit: 0,
                    credit: 0,
                    netMovement: 0
                };
            }

            // Add amount to the appropriate column based on entry type
            if (entryType === 'debit') {
                accountMap[accountId].debit += amount;
                accountMap[accountId].netMovement += amount;
            } else if (entryType === 'credit') {
                accountMap[accountId].credit += amount;
                accountMap[accountId].netMovement -= amount;
            }
        });

        // Convert to array and calculate trial balance amounts (excluding opening balances)
        const accountsArray = Object.values(accountMap).map(account => {
            // Calculate trial balance position based on account type and NET MOVEMENTS ONLY
            let trialBalanceDebit = 0;
            let trialBalanceCredit = 0;

            // Use only the net movement (exclude opening balance)
            const movementBalance = account.netMovement;

            // Determine trial balance presentation based on account type and movement
            if (['asset', 'expense'].includes(account.accountType)) {
                // Asset and expense accounts: positive movement = debit
                if (movementBalance > 0) {
                    trialBalanceDebit = movementBalance;
                } else if (movementBalance < 0) {
                    trialBalanceCredit = Math.abs(movementBalance);
                }
            } else {
                // Liability, equity, revenue accounts: positive movement = credit
                if (movementBalance > 0) {
                    trialBalanceCredit = movementBalance;
                } else if (movementBalance < 0) {
                    trialBalanceDebit = Math.abs(movementBalance);
                }
            }

            return {
                ...account,
                finalBalance: account.openingBalance + account.netMovement, // For reference only
                trialBalanceDebit,
                trialBalanceCredit
            };
        }).filter(account => account.trialBalanceDebit > 0 || account.trialBalanceCredit > 0); // Only show accounts with movements

        console.log('Processed accounts for trial balance:', accountsArray);

        // Calculate totals
        const totalDebit = accountsArray.reduce((sum, account) => sum + account.trialBalanceDebit, 0);
        const totalCredit = accountsArray.reduce((sum, account) => sum + account.trialBalanceCredit, 0);
        console.log('Trial Balance Totals - Debit:', totalDebit, 'Credit:', totalCredit);
        const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01; // Allow for small rounding errors

        // Extract unique account types for filter
        const types = [...new Set(accountsArray.map(account => account.accountType))];
        setAccountTypes(types.map(type => ({
            label: type.charAt(0).toUpperCase() + type.slice(1),
            value: type
        })));

        // Set trial balance data
        setTrialBalanceData({
            asOfDate: endDate ? endDate.toLocaleDateString() : new Date().toLocaleDateString(),
            dateRange: {
                start: startDate ? startDate.toLocaleDateString() : null,
                end: endDate ? endDate.toLocaleDateString() : null
            },
            accounts: accountsArray,
            totalDebit,
            totalCredit,
            isBalanced
        });
    };



    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: selectedCurrency,
            minimumFractionDigits: 2
        }).format(value);
    };

    const debitTemplate = (rowData) => {
        return rowData.trialBalanceDebit > 0 ? formatCurrency(rowData.trialBalanceDebit) : '';
    };

    const creditTemplate = (rowData) => {
        return rowData.trialBalanceCredit > 0 ? formatCurrency(rowData.trialBalanceCredit) : '';
    };

    const generatePDF = () => {
        if (!trialBalanceData) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'No data available to export'
            });
            return;
        }

        try {
            const doc = new jsPDF();

            // Add title
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('Trial Balance', 105, 20, { align: 'center' });

            // Add date range
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            const dateText = trialBalanceData.dateRange.start
                ? `From ${trialBalanceData.dateRange.start} to ${trialBalanceData.dateRange.end}`
                : `As of ${trialBalanceData.asOfDate}`;
            doc.text(dateText, 105, 30, { align: 'center' });

            // Add balance status
            doc.setFontSize(10);
            const balanceText = trialBalanceData.isBalanced ? 'BALANCED' : 'UNBALANCED';
            const balanceColor = trialBalanceData.isBalanced ? [0, 128, 0] : [255, 0, 0];
            doc.setTextColor(...balanceColor);
            doc.text(balanceText, 105, 38, { align: 'center' });
            doc.setTextColor(0, 0, 0); // Reset to black

            // Prepare table data
            const tableData = filteredAccounts.map(account => [
                account.code,
                account.name,
                account.trialBalanceDebit > 0 ? formatCurrency(account.trialBalanceDebit) : '',
                account.trialBalanceCredit > 0 ? formatCurrency(account.trialBalanceCredit) : ''
            ]);

            // Add totals row
            tableData.push([
                '',
                'TOTALS',
                formatCurrency(trialBalanceData.totalDebit),
                formatCurrency(trialBalanceData.totalCredit)
            ]);

            // Add difference row
            const difference = trialBalanceData.totalDebit - trialBalanceData.totalCredit;
            if (Math.abs(difference) > 0.01) {
                tableData.push([
                    '',
                    'DIFFERENCE',
                    '',
                    formatCurrency(Math.abs(difference))
                ]);
            }

            // Create table
            doc.autoTable({
                head: [['Code', 'Account Name', 'Debit', 'Credit']],
                body: tableData,
                startY: 45,
                theme: 'striped',
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                styles: {
                    fontSize: 9,
                    cellPadding: 3
                },
                columnStyles: {
                    0: { cellWidth: 30 }, // Code
                    1: { cellWidth: 90 }, // Account Name
                    2: { cellWidth: 35, halign: 'right' }, // Debit
                    3: { cellWidth: 35, halign: 'right' }  // Credit
                },
                didParseCell: function (data) {
                    // Style totals and difference rows
                    if (data.row.index >= tableData.length - (Math.abs(difference) > 0.01 ? 2 : 1)) {
                        data.cell.styles.fontStyle = 'bold';
                        if (data.row.index === tableData.length - 1 && !trialBalanceData.isBalanced) {
                            data.cell.styles.textColor = [255, 0, 0]; // Red for unbalanced difference
                        }
                    }
                }
            });

            // Add footer with generation date
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.text(`Generated on ${new Date().toLocaleString()}`, 20, doc.internal.pageSize.height - 10);
                doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 10);
            }

            // Save the PDF
            const fileName = `trial-balance-${endDate ? endDate.toISOString().split('T')[0] : 'current'}.pdf`;
            doc.save(fileName);

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'PDF exported successfully'
            });

        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to generate PDF: ' + error.message
            });
        }
    };

    const handleExport = () => {
        generatePDF();
    };

    // Filter accounts based on search term and selected account type
    const filteredAccounts = trialBalanceData?.accounts.filter(account => {
        const matchesSearch = searchTerm === '' ||
            account.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            account.name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesType = selectedAccountType === null || account.accountType === selectedAccountType;

        return matchesSearch && matchesType;
    }) || [];

    return (
        <div className="trial-balance-report">
            <Toast ref={toast} />

            <div className="card mb-4">
                <div className="card-body">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <h4 className="mb-3 mb-md-0">Trial Balance</h4>

                        <div className="d-flex flex-wrap gap-2">
                            <div className="p-field">
                                <label htmlFor="start-date" className="font-weight-bold mr-2">From:</label>
                                <Calendar
                                    id="start-date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.value)}
                                    showIcon
                                    className="w-auto"
                                    placeholder="Start date (optional)"
                                />
                            </div>
                            <div className="p-field">
                                <label htmlFor="end-date" className="font-weight-bold mr-2">To:</label>
                                <Calendar
                                    id="end-date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.value)}
                                    showIcon
                                    className="w-auto"
                                />
                            </div>
                            <Button
                                label="Refresh"
                                icon="pi pi-refresh"
                                onClick={fetchAccountsAndEntries}
                                className="ml-2"
                            />

                            <Button
                                label="Export PDF"
                                icon="pi pi-file-pdf"
                                onClick={handleExport}
                                className="ml-2 p-button-success"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center p-4">
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                            <p className="mt-3">Loading trial balance data...</p>
                        </div>
                    ) : trialBalanceData ? (
                        <div>
                            <div className="text-center mb-4">
                                <h5>Trial Balance</h5>
                                <p className="text-muted mb-0">
                                    {trialBalanceData.dateRange.start
                                        ? `From ${trialBalanceData.dateRange.start} to ${trialBalanceData.dateRange.end}`
                                        : `As of ${trialBalanceData.asOfDate}`
                                    }
                                </p>
                                {trialBalanceData.isBalanced ? (
                                    <div className="badge bg-success mt-2">Balanced</div>
                                ) : (
                                    <div className="badge bg-danger mt-2">Unbalanced</div>
                                )}
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="p-input-icon-left">
                                        <i className="pi pi-search" />
                                        <InputText
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search accounts"
                                        />
                                    </span>

                                    <Dropdown
                                        value={selectedAccountType}
                                        options={[{ label: 'All Account Types', value: null }, ...accountTypes]}
                                        onChange={(e) => setSelectedAccountType(e.value)}
                                        placeholder="Filter by type"
                                        className="w-auto"
                                    />
                                </div>

                                <div>
                                    <span className="mr-3">
                                        <strong>Total:</strong> {filteredAccounts.length} of {trialBalanceData.accounts.length} accounts
                                    </span>
                                    <span className="mr-3">
                                        <strong>Entries:</strong> {glEntries.length} GL entries
                                    </span>
                                </div>
                            </div>

                            <DataTable
                                value={filteredAccounts}
                                className="p-datatable-sm"
                                sortField="code"
                                sortOrder={1}
                                paginator
                                rows={20}
                                emptyMessage="No accounts found"
                                rowClassName={(rowData) =>
                                    rowData.accountType === 'asset' ? 'bg-light-success' :
                                        rowData.accountType === 'liability' ? 'bg-light-danger' :
                                            rowData.accountType === 'equity' ? 'bg-light-primary' :
                                                rowData.accountType === 'revenue' ? 'bg-light-info' :
                                                    'bg-light-warning'
                                }
                            >
                                <Column field="code" header="Account Code" sortable style={{ width: '20%' }} />
                                <Column field="name" header="Account Name" sortable style={{ width: '60%' }} />
                                <Column field="trialBalanceDebit" header="Debit" body={debitTemplate} sortable style={{ width: '20%' }} />
                                <Column field="trialBalanceCredit" header="Credit" body={creditTemplate} sortable style={{ width: '20%' }} />
                            </DataTable>

                            <div className="card bg-light mt-4">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-8 offset-md-2">
                                            <table className="table table-borderless">
                                                <tbody>
                                                    <tr>
                                                        <td className="text-end"><strong>Total Debits:</strong></td>
                                                        <td className="text-end">{formatCurrency(trialBalanceData.totalDebit)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-end"><strong>Total Credits:</strong></td>
                                                        <td className="text-end">{formatCurrency(trialBalanceData.totalCredit)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-end border-top"><strong>Difference:</strong></td>
                                                        <td className={`text-end border-top ${trialBalanceData.isBalanced ? 'text-success' : 'text-danger'}`}>
                                                            <strong>{formatCurrency(Math.abs(trialBalanceData.totalDebit - trialBalanceData.totalCredit))}</strong>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center p-4">
                            <p>No trial balance data available</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrialBalance;